/**
 * The Clients cell: tick who this applies to.
 *
 * It was a free-text box holding a comma-separated list, which is a large part of how the log
 * came to say "All" on well over half the rows she had tagged — typing three names is work and
 * typing one word is not. She is now going through the log by hand putting the right clients
 * on each entry, and that is a job of ticking boxes, not of spelling names a filter has to
 * match exactly.
 *
 * Three things here are deliberate.
 *
 * **"All clients" is a master toggle, not a value.** It ticks and unticks the live clients,
 * and what leaves this component is always the names. Her cell still reads "All" — the server
 * derives that from the selection (`reghorizon.clients.label`) rather than storing the word —
 * so pressing this and ticking every box by hand leave the log in identical states. They mean
 * the same thing, so they must not be distinguishable afterwards.
 *
 * **An archived client already on the item is shown, and ticked.** Nine items name Senguard,
 * Morris or Alessia, who are archived and so are not offered. If the picker listed only the
 * live three, opening one of those rows and pressing Done would silently drop her history —
 * the worst kind of loss, because nothing in the interaction suggests anything was removed.
 *
 * **Anything in her raw cell that is not a client is shown as a note.** "check" and "Cyber"
 * are notes she wrote to herself in the Client applicability column; saving a selection
 * replaces that cell, so it is worth seeing before it goes rather than after.
 */

import type { CustomCellEditorProps } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Item } from "../types";
import { ALL_CLIENTS_TAG, coversLive, toggleClient } from "../types";

interface Props extends CustomCellEditorProps<Item, string> {
  /** The clients a tag can name: everything not archived, in the Clients screen's order. */
  live: string[];
}

export function ClientPicker({ data, live, onValueChange, stopEditing }: Props) {
  const [chosen, setChosen] = useState<string[]>(() => [...(data?.clients ?? [])]);
  const container = useRef<HTMLDivElement>(null);

  /**
   * Names on the item that are not on offer — archived clients, and anything she typed before
   * this picker existed. Listed after the live ones so the working set stays at the top.
   */
  const alsoNamed = useMemo(
    () => (data?.clients ?? []).filter((name) => !live.includes(name)),
    [data, live],
  );

  /** Her own cell, when it holds something that is not a client at all. */
  const note = useMemo(() => {
    const raw = (data?.clients_raw ?? "").trim();
    if (!raw || raw === ALL_CLIENTS_TAG || (data?.clients ?? []).length) return "";
    return raw;
  }, [data]);

  /**
   * Every change is handed straight to the grid, which is what the custom-editor contract asks
   * for and is also what makes clicking away safe: AG Grid tears the editor down on an outside
   * click and takes the value it was last given. Staging locally and committing only on Done
   * would lose her ticks with nothing on screen to say so.
   *
   * The order is the item's: live clients as the Clients screen lists them, then anything else
   * already on the row. Stable, so re-opening a cell and pressing Done writes nothing and the
   * audit trail records her decisions rather than her visits.
   */
  const choose = (names: string[]) => {
    setChosen(names);
    const ordered = [...live, ...alsoNamed].filter((name) => names.includes(name));
    onValueChange(ordered.join(", "));
  };

  const toggle = (name: string) => choose(toggleClient(chosen, name, live));
  const toggleAll = () => choose(toggleClient(chosen, ALL_CLIENTS_TAG, live));

  // Ticked when every live client is on the item, even if an archived one is too — see
  // `coversLive`. The cell still spells the names out in that case; only the box is a
  // summary, and a box that cannot be cleared is worse than one that reads loosely.
  const everyone = coversLive(chosen, live);

  // Enter closes the picker, because that is what Enter does in every other cell in this grid.
  // Escape cancels, and the grid discards the staged value — nothing has reached the server.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      stopEditing();
    }
    if (event.key === "Escape") stopEditing(true);
  };

  useEffect(() => container.current?.focus(), []);

  return (
    <div className="client-picker" ref={container} tabIndex={-1} onKeyDown={onKeyDown}>
      <label className={everyone ? "row all on" : "row all"}>
        <input type="checkbox" checked={everyone} onChange={toggleAll} />
        <span>All clients</span>
      </label>

      <div className="rows">
        {live.map((name) => (
          <label key={name} className={chosen.includes(name) ? "row on" : "row"}>
            <input type="checkbox" checked={chosen.includes(name)} onChange={() => toggle(name)} />
            <span>{name}</span>
          </label>
        ))}

        {alsoNamed.map((name) => (
          <label key={name} className={chosen.includes(name) ? "row on" : "row"}>
            <input type="checkbox" checked={chosen.includes(name)} onChange={() => toggle(name)} />
            <span>{name}</span>
            <span className="muted">archived</span>
          </label>
        ))}
      </div>

      {note && <p className="note">Her cell says “{note}”. Saving a selection replaces it.</p>}

      <div className="picker-actions">
        <button type="button" className="link" onClick={() => choose([])}>
          Clear
        </button>
        <button type="button" onClick={() => stopEditing()}>
          Done
        </button>
      </div>
    </div>
  );
}
